import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Table } from "react-bootstrap";
import { FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SettingsWorkbenchLayout, {
  SettingsWorkbenchItem,
} from "@shared/components/SettingsWorkbenchLayout";
import {
  EventCatalogItem,
  EventCatalogType,
  EventSet,
} from "../types/eegConfig.types";
import {
  fetchEventCatalog,
  fetchEventSets,
  saveEventCatalogBundle,
} from "../services/eegConfigService";
import {
  getNonAuthSearchInputProps,
  getPasswordManagerIgnoreFormProps,
} from "@shared/utils/nonAuthPasswordFields";
import EegConfigPageShell from "../components/EegConfigPageShell";
import { fetchElectrodeSystemAccess } from "../../Montage/services/MontageService";
import "../styles/eegConfig.css";

const EVENT_TYPES: EventCatalogType[] = [
  "episode",
  "annotation",
  "context",
  "duration",
  "prune",
];

const createDraftEvent = (
  eventSetId: string,
  name = "New Event",
  category = "Clinical",
  priority: EventCatalogItem["priority"] = "Normal",
): EventCatalogItem => ({
  id: `event-${crypto.randomUUID()}`,
  name,
  description: "",
  types: ["annotation"],
  color: "#0d6efd",
  category,
  priority,
  eventSetIds: [eventSetId],
  annotationTypeCode: "",
  visibleInDock: true,
  defaultQuickAction: false,
});

const createDraftEventSet = (name = "New Event Set"): EventSet => ({
  id: `event-set-${crypto.randomUUID()}`,
  name,
  description: "",
  eventIds: [],
  isSystem: false,
});

const EventCatalogPage: React.FC = () => {
  const { t } = useTranslation("features");
  const [eventSets, setEventSets] = useState<EventSet[]>([]);
  const [events, setEvents] = useState<EventCatalogItem[]>([]);
  const [selectedEventSetId, setSelectedEventSetId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [eventSetRows, eventRows, access] = await Promise.all([
          fetchEventSets(), fetchEventCatalog(), fetchElectrodeSystemAccess(),
        ]);
        if (cancelled) return;
        setEventSets(eventSetRows);
        setEvents(eventRows);
        setCanManage(access.can_manage_org_eeg_config);
        setSelectedEventSetId(eventSetRows[0]?.id || "");
      } catch {
        if (!cancelled) setLoadError("Could not load organization event configuration. No local defaults were substituted.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedEventSet = eventSets.find((item) => item.id === selectedEventSetId);

  const filteredEventSets: SettingsWorkbenchItem[] = useMemo(
    () =>
      eventSets
        .filter((eventSet) =>
          `${eventSet.name} ${eventSet.description || ""}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
        .map((eventSet) => ({
          id: eventSet.id,
          title: eventSet.name,
          subtitle: eventSet.description,
          badge: eventSet.isSystem
            ? t("ui.eegConfig.eventCatalog.systemBadge", {
                defaultValue: "System",
              })
            : t("ui.eegConfig.eventCatalog.customBadge", {
                defaultValue: "Custom",
              }),
          locked: eventSet.isSystem,
        })),
    [eventSets, search, t],
  );

  const eventSetEvents = useMemo(() => {
    if (!selectedEventSet) return [];
    return events.filter((event) => event.eventSetIds.includes(selectedEventSet.id));
  }, [events, selectedEventSet]);

  const updateEvent = (
    eventId: string,
    patch: Partial<EventCatalogItem>,
  ) => {
    setEvents((current) =>
      current.map((event) =>
        event.id === eventId ? { ...event, ...patch } : event,
      ),
    );
  };

  const updateEventSet = (eventSetId: string, patch: Partial<EventSet>) => {
    setEventSets((current) =>
      current.map((eventSet) =>
        eventSet.id === eventSetId ? { ...eventSet, ...patch } : eventSet,
      ),
    );
  };

  const toggleEventType = (event: EventCatalogItem, type: EventCatalogType) => {
    const nextTypes = event.types.includes(type)
      ? event.types.filter((item) => item !== type)
      : [...event.types, type];
    updateEvent(event.id, { types: nextTypes.length ? nextTypes : ["annotation"] });
  };

  const addEvent = () => {
    if (!selectedEventSetId) return;
    const draft = createDraftEvent(
      selectedEventSetId,
      t("ui.eegConfig.eventCatalog.newEvent", {
        defaultValue: "New Event",
      }),
      t("ui.eegConfig.eventCatalog.defaultCategory", {
        defaultValue: "Clinical",
      }),
    );
    setEvents((current) => [draft, ...current]);
    updateEventSet(selectedEventSetId, {
      eventIds: [...(selectedEventSet?.eventIds || []), draft.id],
    });
    setHighlightedEventId(draft.id);
    toast.success(
      t("ui.eegConfig.eventCatalog.eventAdded", {
        defaultValue: "New event added — edit the name, then Save.",
      }),
    );
    window.setTimeout(() => {
      const row = document.querySelector<HTMLElement>(
        `[data-event-id="${draft.id}"]`,
      );
      const nameInput = row?.querySelector<HTMLInputElement>("input.form-control");
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      nameInput?.focus();
      nameInput?.select();
    }, 0);
    window.setTimeout(() => {
      setHighlightedEventId((current) =>
        current === draft.id ? null : current,
      );
    }, 2500);
  };

  const deleteEvent = (eventId: string) => {
    setEvents((current) => current.filter((event) => event.id !== eventId));
    setEventSets((current) =>
      current.map((eventSet) => ({
        ...eventSet,
        eventIds: eventSet.eventIds.filter((id) => id !== eventId),
      })),
    );
  };

  const addEventSet = () => {
    const draft = createDraftEventSet(
      t("ui.eegConfig.eventCatalog.newEventSet", {
        defaultValue: "New Event Set",
      }),
    );
    setEventSets((current) => [draft, ...current]);
    setSelectedEventSetId(draft.id);
  };

  const deleteEventSet = () => {
    if (!selectedEventSet) return;
    setEventSets((current) =>
      current.filter((eventSet) => eventSet.id !== selectedEventSet.id),
    );
    setEvents((current) =>
      current.map((event) => ({
        ...event,
        eventSetIds: event.eventSetIds.filter((id) => id !== selectedEventSet.id),
      })),
    );
    const remaining = eventSets.filter(
      (eventSet) => eventSet.id !== selectedEventSet.id,
    );
    setSelectedEventSetId(remaining[0]?.id || "");
  };

  const saveCatalog = async () => {
    if (!canManage) return;
    try {
      const saved = await saveEventCatalogBundle(eventSets, events);
      setEventSets(saved.eventSets);
      setEvents(saved.events);
      toast.success(
        t("ui.eegConfig.eventCatalog.saved", {
          defaultValue: "Event catalog saved.",
        }),
      );
    } catch {
      toast.error(
        t("ui.eegConfig.eventCatalog.saveFailed", {
          defaultValue: "Failed to save event catalog.",
        }),
      );
    }
  };

  return (
    <EegConfigPageShell className="app-ui-surface">
      {loadError && <Alert variant="danger" role="alert">{loadError}</Alert>}
      {!canManage && !loadError && !loading && <Alert variant="info">You can view this catalog, but cannot change organization EEG configuration.</Alert>}
      <SettingsWorkbenchLayout
        title={t("ui.eegConfig.eventCatalog.title", {
          defaultValue: "Event Catalog",
        })}
        description={t("ui.eegConfig.eventCatalog.description", {
          defaultValue:
            "Manage EEG event types and group them into event sets used by Perspectives.",
        })}
        listTitle={t("ui.eegConfig.eventCatalog.listTitle", {
          defaultValue: "Event Sets",
        })}
        items={filteredEventSets}
        selectedItemId={selectedEventSetId}
        searchValue={search}
        searchPlaceholder={t("ui.eegConfig.eventCatalog.searchPlaceholder", {
          defaultValue: "Search event sets",
        })}
        loading={loading}
        onSearchChange={setSearch}
        onSelectItem={(item) => setSelectedEventSetId(item.id)}
        footerActions={
          <Button type="button" size="sm" variant="outline-primary" onClick={addEventSet} disabled={!canManage}>
            <FaPlus className="me-1" />
            <Trans ns="features" i18nKey="ui.copy.52b560c91c6b" /></Button>
        }
        actions={
          <div className="app-ui-stack-actions">
            <Button type="button" variant="primary" onClick={saveCatalog} disabled={!canManage}>
              <FaSave className="me-1" />
              <Trans ns="features" i18nKey="ui.copy.567e12738ddb" />
            </Button>
          </div>
        }
      >
        <fieldset disabled={!canManage} className="border-0 p-0 m-0">
        <Card className="app-ui-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="flex-grow-1 me-3">
              {selectedEventSet ? (
                <div
                  className="eeg-config-event-set-editor"
                  {...getPasswordManagerIgnoreFormProps()}
                >
                  <Form.Control
                    value={selectedEventSet.name}
                    onChange={(event) =>
                      updateEventSet(selectedEventSet.id, {
                        name: event.target.value,
                      })
                    }
                    aria-label={t("ui.eegConfig.eventCatalog.eventSetName", {
                      defaultValue: "Event Set name",
                    })}
                    {...getNonAuthSearchInputProps("event-set-name")}
                  />
                  <Form.Control
                    className="mt-2"
                    size="sm"
                    value={selectedEventSet.description || ""}
                    placeholder={t(
                      "ui.eegConfig.eventCatalog.eventSetDescription",
                      { defaultValue: "Event Set description" },
                    )}
                    onChange={(event) =>
                      updateEventSet(selectedEventSet.id, {
                        description: event.target.value,
                      })
                    }
                    aria-label={t(
                      "ui.eegConfig.eventCatalog.eventSetDescription",
                      { defaultValue: "Event Set description" },
                    )}
                    {...getNonAuthSearchInputProps("event-set-description")}
                  />
                </div>
              ) : (
                <strong><Trans ns="features" i18nKey="ui.copy.14845806b9b9" /></strong>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                onClick={deleteEventSet}
                disabled={!selectedEventSet || selectedEventSet.isSystem}
              >
                <FaTrash className="me-1" />
                <Trans ns="features" i18nKey="ui.copy.52b560c91c6b" /></Button>
              <Button
                type="button"
                size="sm"
                onClick={addEvent}
                disabled={!selectedEventSetId}
              >
                <FaPlus className="me-1" />
                <Trans ns="features" i18nKey="ui.copy.ad8919ace091" /></Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div
              className="table-responsive eeg-config-table-wrap"
              tabIndex={0}
              role="region"
              aria-label="Event catalog table"
            >
              <Table hover className="mb-0 eeg-config-table">
                <colgroup>
                  <col className="eeg-config-event-col-name" />
                  <col className="eeg-config-event-col-types" />
                  <col className="eeg-config-event-col-category" />
                  <col className="eeg-config-event-col-priority" />
                  <col className="eeg-config-event-col-signal" />
                  <col className="eeg-config-event-col-color" />
                  <col className="eeg-config-event-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th><Trans ns="features" i18nKey="ui.copy.709a23220f2c" /></th>
                    <th className="eeg-config-table__cell--types">
                      <Trans ns="features" i18nKey="ui.copy.93b9e289e284" />
                    </th>
                    <th className="eeg-config-table__cell--category">
                      <Trans ns="features" i18nKey="ui.copy.a3c686e711e4" />
                    </th>
                    <th><Trans ns="features" i18nKey="ui.copy.886cbff9d9df" /></th>
                    <th><Trans ns="features" i18nKey="ui.copy.27bed13a88c6" /></th>
                    <th className="eeg-config-table__cell--color">
                      <Trans ns="features" i18nKey="ui.copy.1d0c8304baed" />
                    </th>
                    <th
                      className="eeg-config-table__cell--actions"
                      aria-label={t("ui.eegConfig.eventCatalog.actions", {
                        defaultValue: "Actions",
                      })}
                    />
                  </tr>
                </thead>
                <tbody>
                  {eventSetEvents.map((event) => (
                    <React.Fragment key={event.id}>
                      <tr
                        className={`eeg-config-event-row eeg-config-event-row--primary${
                          highlightedEventId === event.id
                            ? " eeg-config-event-row--new"
                            : ""
                        }`}
                        data-event-id={event.id}
                      >
                        <td>
                          <Form.Control
                            value={event.name}
                            onChange={(input) =>
                              updateEvent(event.id, { name: input.target.value })
                            }
                            aria-label={t("ui.eegConfig.eventCatalog.eventName", {
                              defaultValue: "Event name",
                            })}
                            {...getNonAuthSearchInputProps(`event-name-${event.id}`)}
                          />
                        </td>
                        <td className="eeg-config-table__cell--types">
                          <div className="eeg-config-chip-list">
                            {EVENT_TYPES.map((type) => (
                              <Form.Check
                                key={type}
                                type="checkbox"
                                label={t(
                                  `ui.eegConfig.eventCatalog.type.${type}`,
                                  { defaultValue: type },
                                )}
                                checked={event.types.includes(type)}
                                onChange={() => toggleEventType(event, type)}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="eeg-config-table__cell--category">
                          <Form.Control
                            value={event.category}
                            onChange={(input) =>
                              updateEvent(event.id, {
                                category: input.target.value,
                              })
                            }
                            aria-label={t("ui.eegConfig.eventCatalog.category", {
                              defaultValue: "Category",
                            })}
                          />
                        </td>
                        <td>
                          <Form.Select
                            value={event.priority}
                            onChange={(input) =>
                              updateEvent(event.id, {
                                priority: input.target
                                  .value as EventCatalogItem["priority"],
                              })
                            }
                            aria-label={t("ui.eegConfig.eventCatalog.priorityLabel", {
                              defaultValue: "Priority",
                            })}
                          >
                            <option value="Low">
                              {t("ui.eegConfig.eventCatalog.priority.low", {
                                defaultValue: "Low",
                              })}
                            </option>
                            <option value="Normal">
                              {t("ui.eegConfig.eventCatalog.priority.normal", {
                                defaultValue: "Normal",
                              })}
                            </option>
                            <option value="High">
                              {t("ui.eegConfig.eventCatalog.priority.high", {
                                defaultValue: "High",
                              })}
                            </option>
                            <option value="Critical">
                              {t("ui.eegConfig.eventCatalog.priority.critical", {
                                defaultValue: "Critical",
                              })}
                            </option>
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            value={event.smartSignalType || ""}
                            placeholder={t("ui.eegConfig.eventCatalog.optional", {
                              defaultValue: "Optional",
                            })}
                            onChange={(input) =>
                              updateEvent(event.id, {
                                smartSignalType: input.target.value,
                              })
                            }
                            aria-label={t("ui.eegConfig.eventCatalog.signalType", {
                              defaultValue: "Signal type",
                            })}
                          />
                        </td>
                        <td className="eeg-config-table__cell--color">
                          <div className="eeg-config-color-field">
                            <Form.Control
                              type="color"
                              value={event.color}
                              onChange={(input) =>
                                updateEvent(event.id, {
                                  color: input.target.value,
                                })
                              }
                              aria-label={t("ui.eegConfig.eventCatalog.color", {
                                defaultValue: "Color",
                              })}
                            />
                            <Badge bg="light" text="dark" className="eeg-config-color-badge">
                              {event.color}
                            </Badge>
                          </div>
                        </td>
                        <td className="eeg-config-table__cell--actions">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-danger"
                            onClick={() => deleteEvent(event.id)}
                            aria-label={t("ui.eegConfig.eventCatalog.deleteEvent", {
                              name: event.name,
                              defaultValue: "Delete {{name}}",
                            })}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                      <tr className="eeg-config-event-row eeg-config-event-row--details">
                        <td colSpan={7}>
                          <div className="eeg-config-event-details">
                            <Form.Group className="eeg-config-event-details__field">
                              <Form.Label className="eeg-config-event-details__label">
                                {t("ui.eegConfig.eventCatalog.annotationTypeCode", {
                                  defaultValue: "Annotation type code",
                                })}
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={event.annotationTypeCode || ""}
                                placeholder={t(
                                  "ui.eegConfig.eventCatalog.annotationTypeCode",
                                  { defaultValue: "Annotation type code" },
                                )}
                                onChange={(input) =>
                                  updateEvent(event.id, {
                                    annotationTypeCode: input.target.value,
                                  })
                                }
                              />
                            </Form.Group>
                            <Form.Group className="eeg-config-event-details__field eeg-config-event-details__field--wide">
                              <Form.Label className="eeg-config-event-details__label">
                                {t("ui.eegConfig.eventCatalog.eventDescription", {
                                  defaultValue: "Description",
                                })}
                              </Form.Label>
                              <Form.Control
                                size="sm"
                                value={event.description || ""}
                                placeholder={t(
                                  "ui.eegConfig.eventCatalog.eventDescription",
                                  { defaultValue: "Description" },
                                )}
                                onChange={(input) =>
                                  updateEvent(event.id, {
                                    description: input.target.value,
                                  })
                                }
                              />
                            </Form.Group>
                            <div className="eeg-config-event-details__switches">
                              <Form.Check
                                type="switch"
                                id={`${event.id}-visible-in-dock`}
                                label={t("ui.eegConfig.eventCatalog.visibleInDock", {
                                  defaultValue: "Visible in Dock",
                                })}
                                checked={event.visibleInDock !== false}
                                onChange={(input) =>
                                  updateEvent(event.id, {
                                    visibleInDock: input.target.checked,
                                  })
                                }
                              />
                              <Form.Check
                                type="switch"
                                id={`${event.id}-quick-action`}
                                label={t("ui.eegConfig.eventCatalog.eventSetQuickAction", {
                                  defaultValue: "Event Set quick action",
                                })}
                                checked={Boolean(event.defaultQuickAction)}
                                onChange={(input) =>
                                  updateEvent(event.id, {
                                    defaultQuickAction: input.target.checked,
                                    defaultShortcut: input.target.checked
                                      ? event.defaultShortcut
                                      : undefined,
                                  })
                                }
                              />
                              {event.defaultQuickAction && (
                                <Form.Select
                                  size="sm"
                                  className="eeg-config-event-details__shortcut"
                                  aria-label={t("ui.eegConfig.eventCatalog.defaultShortcut", {
                                    defaultValue: "Default shortcut",
                                    name: event.name,
                                  })}
                                  value={event.defaultShortcut || ""}
                                  onChange={(input) =>
                                    updateEvent(event.id, {
                                      defaultShortcut: input.target.value
                                        ? Number(input.target.value)
                                        : undefined,
                                    })
                                  }
                                >
                                  <option value="">
                                    {t("ui.eegConfig.eventCatalog.autoShortcut", {
                                      defaultValue: "Auto shortcut",
                                    })}
                                  </option>
                                  {[1, 2, 3, 4, 5, 6].map((shortcut) => (
                                    <option key={shortcut} value={shortcut}>
                                      {t("ui.eegConfig.eventCatalog.shortcutNumber", {
                                        defaultValue: "Shortcut {{number}}",
                                        number: shortcut,
                                      })}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  {eventSetEvents.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center app-list-item__meta p-4">
                        <Trans ns="features" i18nKey="ui.copy.7f3af1420ea2" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
        </fieldset>
      </SettingsWorkbenchLayout>
    </EegConfigPageShell>
  );
};

export default EventCatalogPage;
